import React, { useState } from 'react';
import { Card, Button, Space, Input, Row, Col, Grid, List, message, Pagination, Progress } from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { DataTable } from '../../../components/DataTable';
import type { AntDColumn } from '../../../components/DataTable';
import { Block } from '@/types/resources/property';
import { BlockFormModal } from '@/components/features/resources/properties/BlockFormModal';
import { BlockBulkUploadModal } from '@/components/features/resources/properties/BlockBulkUploadModal';
import { getBlocks, addBlock, updateBlock, deleteBlock, forceDeleteBlock } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import { getAllTotals, hasWorkspaceStats } from '@/utils/workspaceStats';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { useBreakpoint } = Grid;

// Constants
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const Blocks: React.FC = () => {
  const { t } = useTranslation('properties');
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBlock, setEditingBlock] = useState<Partial<Block> | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState<Block | null>(null);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; blockName?: string } | undefined>(undefined);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const { firestoreUser, workspaceStats } = useAuth();
  
  // Check if this is the active tab
  const isActiveTab = location.pathname.includes('/blocks');
  
  // Queries
  const { data: blocksData = { blocks: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery<{ blocks: Block[]; lastDoc?: QueryDocumentSnapshot; total: number }>({
    queryKey: ['blocks', currentPage, pageSize, searchTerm, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 1] : undefined;
      return getBlocks({
        searchTerm,
        pageSize,
        currentPage,
        lastDoc,
        firestoreUser
      });
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser && isActiveTab, // Only run query if firestoreUser is available AND this is the active tab
  });

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (blocksData.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = blocksData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [blocksData.lastDoc, currentPage]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setLastDocs([]);
  }, [searchTerm, pageSize]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (block: Omit<Block, 'id' | 'activityRecords' | 'workspace'>) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return addBlock(block, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('blocks.messages.addError'));
      console.error('Error adding block:', error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Block> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateBlock(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('blocks.messages.updateError'));
      console.error('Error updating block:', error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteBlock(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks'] });
      setShowDeleteModal(false);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error);
      } else {
        message.error(t('blocks.messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting block:', error);
    }
  });

  const forceDeleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return forceDeleteBlock(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('blocks.messages.forceDeleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['blocks'] });
      setShowDeleteModal(false);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      message.error(t('blocks.messages.forceDeleteError'));
      setShowDeleteModal(false);
      console.error('Error force deleting block:', error);
    }
  });

  const handleAdd = () => {
    setEditingBlock(undefined);
    setIsModalVisible(true);
  };

  const handleEdit = (block: Block) => {
    setEditingBlock(block);
    setIsModalVisible(true);
  };

  const handleDelete = (block: Block) => {
    setBlockToDelete(block);
    setShowDeleteModal(true);
  };

  const handleView = (block: Block) => {
    navigate(`/resources/properties/blocks/${block.id}`, { state: { block } });
  };

  const handleBulkDelete = (selectedRowKeys: React.Key[]) => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk delete:', selectedRowKeys);
  };

  const handleBulkUpload = () => {
    setShowBulkUploadModal(true);
  };

  const handleBulkUploadSuccess = () => {
    setShowBulkUploadModal(false);
    queryClient.invalidateQueries({ queryKey: ['blocks'] });
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const handleModalSubmit = async (values: { block: Partial<Block>; floors: any[] }): Promise<string> => {
    if (editingBlock?.id) {
      updateMutation.mutate({ id: editingBlock.id, data: values.block });
      return editingBlock.id; // Return existing ID for edit mode
    } else {
      // Ensure required fields are present
      if (!values.block.name) {
        message.error(t('blocks.messages.nameRequired'));
        throw new Error('Block name is required');
      }
      
      // For new blocks, we need to create them and return the ID
      // Since we're using mutations, we'll simulate the async behavior
      return new Promise((resolve, reject) => {
        addMutation.mutate(values.block as Omit<Block, 'id'>, {
          onSuccess: (data) => {
            resolve(data); // Return the created block ID
          },
          onError: (error) => {
            reject(error);
          }
        });
      });
    }
  };

  const blockColumns: AntDColumn<Block>[] = [
    {
      title: t('blocks.fields.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: t('blocks.fields.description'),
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: t('blocks.fields.floors'),
      dataIndex: 'floors',
      key: 'floors',
      render: (floors: any[] | undefined) => <span>{floors?.length || 0}</span>,
    },
  ];

  const getFilteredData = () => {
    if (!blocksData.blocks) return [];
    // For demo mode, we'll do client-side filtering for additional search terms
    // In a real implementation, all filtering would be done by the API
    const lowerSearchTerm = searchTerm.toLowerCase();
    return blocksData.blocks.filter(block => 
      block.name.toLowerCase().includes(lowerSearchTerm) ||
      (block.description ?? '').toLowerCase().includes(lowerSearchTerm)
    );
  };

  const renderBlockCard = (block: Block) => (
    <Card
      key={block.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(block)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(block)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(block)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={block.name}
        description={
          <Space direction="vertical" size="small">
            <div>{block.description}</div>
            <div>{t('blocks.fields.floors')}: {block.floors?.length || 0}</div>
          </Space>
        }
      />
    </Card>
  );

  return (
    <div style={{ width: '100%' }}>
      {/* Block Statistics Section */}
      {workspaceStats && hasWorkspaceStats(workspaceStats) && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={8}>
            <Card 
              title="Property Overview" 
              size="small"
              style={{ borderRadius: '8px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Total Blocks</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                  {workspaceStats.blocks ? Object.keys(workspaceStats.blocks).length : 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Average Units/Block</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                  {Math.round(getAllTotals(workspaceStats).totalApartmentUnits / getAllTotals(workspaceStats).totalBlocks)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Parking Ratio</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                  {getAllTotals(workspaceStats).totalApartmentUnits > 0 
                    ? (getAllTotals(workspaceStats).totalParkings / getAllTotals(workspaceStats).totalApartmentUnits).toFixed(1)
                    : '0'
                  }
                </span>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={8}>
            <Card 
              title="Occupancy Status" 
              size="small"
              style={{ borderRadius: '8px' }}
            >
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px' }}>Occupied</span>
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>
                    {getAllTotals(workspaceStats).totalResidents}
                  </span>
                </div>
                <Progress 
                  percent={getAllTotals(workspaceStats).totalApartmentUnits > 0 
                    ? Math.round((getAllTotals(workspaceStats).totalResidents / getAllTotals(workspaceStats).totalApartmentUnits) * 100)
                    : 0
                  } 
                  size="small"
                  strokeColor="#52c41a"
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Vacant Units</span>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#faad14' }}>
                  {getAllTotals(workspaceStats).totalApartmentUnits - getAllTotals(workspaceStats).totalResidents}
                </span>
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={24} lg={8}>
            <Card 
              title="Amenity Distribution" 
              size="small"
              style={{ borderRadius: '8px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Active Amenities</span>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#52c41a' }}>
                  {getAllTotals(workspaceStats).totalAmenities}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Blocks with Amenities</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                  {workspaceStats.blocks ? Object.values(workspaceStats.blocks).filter(block => block.totalAmenities > 0).length : 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.65)' }}>Avg Amenities/Block</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                  {Math.round(getAllTotals(workspaceStats).totalAmenities / getAllTotals(workspaceStats).totalBlocks)}
                </span>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {screens.xs ? (
        <>
          <List
            dataSource={getFilteredData()}
            renderItem={renderBlockCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            loading={isLoading}
            pagination={false}
            header={
              <Row className="search-filter-section" gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]} style={{ marginBottom: 16 }} align="middle">
                <Col xs={24} sm={16} md={18}>
                  <Input
                    className="search-input"
                    id="blocks-search-input"
                    placeholder={t('common:search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={8} md={6} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? 8 : 0 }}>
                  <Button className="add-record-button" id="blocks-add-button" type="primary" onClick={handleAdd} icon={<PlusOutlined />} block={screens.xs}>
                    {t('blocks.actions.add')}
                  </Button>
                </Col>
              </Row>
            }
          />
          <div className="pagination-section" style={{ 
            marginTop: 16, 
            textAlign: 'center',
            padding: '0 8px'
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={blocksData.total}
              onChange={handlePageChange}
              showSizeChanger={!screens.xs}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showQuickJumper={!screens.xs}
              showTotal={!screens.xs ? (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                }) : undefined
              }
              size={screens.xs ? 'small' : 'default'}
              simple={screens.xs}
              style={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            />
          </div>
        </>
      ) : (
        <DataTable<Block>
          className="data-table"
          columns={blockColumns}
          dataSource={getFilteredData()}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          onBulkDelete={handleBulkDelete}
          onBulkUpload={handleBulkUpload}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: blocksData.total,
            onChange: handlePageChange,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            showTotal: (total: number, range: [number, number]) => 
              t('common:pagination.showing', { 
                start: range[0], 
                end: range[1], 
                total 
              })
          }}
          headerActions={
            <Space>
              <Input
                className="search-input"
                placeholder={t('common:search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                style={{width: 250}}
              />
              <Button className="add-record-button" type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
                {t('blocks.actions.add')}
              </Button>
            </Space>
          }
        />
      )}

      <BlockFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleModalSubmit}
        initialValues={editingBlock}
        title={editingBlock ? t('blocks.actions.edit') : t('blocks.actions.add')}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={() => {
          if (blockToDelete) {
            deleteMutation.mutate(blockToDelete.id);
          }
        }}
        onForceDelete={() => {
          if (blockToDelete) {
            forceDeleteMutation.mutate(blockToDelete.id);
          }
        }}
        title={t('blocks.messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
        isDeleting={deleteMutation.isPending || forceDeleteMutation.isPending}
      />

      <BlockBulkUploadModal
        visible={showBulkUploadModal}
        onCancel={() => setShowBulkUploadModal(false)}
        onSuccess={handleBulkUploadSuccess}
      />
    </div>
  );
}; 